import React, { useState } from 'react';
import { Alert, Button, Empty, Popconfirm, Space, Table, Typography, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useQuery } from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCopy, faDownload } from '@fortawesome/pro-solid-svg-icons';
import { DiscussInterest, getDiscussInterest, removeDiscussInterest } from '../../services/discuss';

const sources = { landing: 'Discuss page', ended: 'End of a discussion' };
const sites = { in: 'ourora.in', uk: 'ourora.co.uk' };

/** "Keep me posted" sign-ups. People were promised one email, when Discuss leaves beta. */
export default function InterestTab({ userId }: { userId?: string }) {
  const [removing, setRemoving] = useState<string | null>(null);
  // Emails are personal data: keep them only while this tab is open.
  const list = useQuery({ queryKey: ['discuss', userId, 'interest'], queryFn: ({ signal }) => getDiscussInterest(signal), gcTime: 0, staleTime: 30_000, refetchOnWindowFocus: false, retry: false });
  const rows = list.data || [];

  const copyAll = async () => {
    try {
      await navigator.clipboard.writeText(rows.map(r => r.email).join(', '));
      message.success(`Copied ${rows.length} ${rows.length === 1 ? 'email' : 'emails'}`);
    } catch {
      message.error('Couldn’t copy. Download the CSV instead.');
    }
  };
  const download = () => {
    const csv = ['email,signed_up_from,site,signed_up_at', ...rows.map(r => [r.email, sources[r.source], sites[r.site], new Date(r.createdAt).toISOString()].map(v => `"${v.replace(/"/g, '""')}"`).join(','))].join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    const a = Object.assign(document.createElement('a'), { href: url, download: `discuss-keep-me-posted-${new Date().toISOString().slice(0, 10)}.csv` });
    a.click();
    URL.revokeObjectURL(url);
  };
  const remove = async (email: string) => {
    setRemoving(email);
    try {
      await removeDiscussInterest(email);
      message.success(`Removed ${email}`);
      await list.refetch();
    } catch {
      message.error('Couldn’t remove that email. Please retry.');
    } finally {
      setRemoving(null);
    }
  };

  const columns: ColumnsType<DiscussInterest> = [
    { title: 'Email', dataIndex: 'email', key: 'email', render: email => <Typography.Text copyable>{email}</Typography.Text> },
    { title: 'Signed up from', key: 'source', width: 200, render: (_, r) => sources[r.source],
      filters: Object.entries(sources).map(([value, text]) => ({ value, text })), onFilter: (v, r) => r.source === v },
    { title: 'Site', key: 'site', width: 140, render: (_, r) => sites[r.site],
      filters: Object.entries(sites).map(([value, text]) => ({ value, text })), onFilter: (v, r) => r.site === v },
    { title: 'Signed up', key: 'at', width: 200, defaultSortOrder: 'descend', sorter: (a, b) => a.createdAt - b.createdAt, render: (_, r) => new Date(r.createdAt).toLocaleString() },
    { title: '', key: 'remove', width: 110, render: (_, r) => <Popconfirm title={`Remove ${r.email}?`} description="Use this for removal requests. It can’t be undone." okText="Remove" okButtonProps={{ danger: true }} onConfirm={() => remove(r.email)}>
      <Button size="small" danger loading={removing === r.email}>Remove</Button>
    </Popconfirm> },
  ];

  if (list.isError) return <Alert type="error" showIcon title="Could not load sign-ups" description="Please retry. Your Core session must have admin access." action={<Button onClick={() => list.refetch()}>Retry</Button>} />;
  return <>
    <div className="discuss-review-usage">
      <strong>{list.isLoading ? 'Loading…' : `${rows.length} ${rows.length === 1 ? 'person' : 'people'} on the list`}</strong>
      <span>{rows.filter(r => r.source === 'landing').length} from the Discuss page · {rows.filter(r => r.source === 'ended').length} after a discussion</span>
      <Space style={{ marginLeft: 'auto' }}>
        <Button icon={<FontAwesomeIcon icon={faCopy} />} disabled={!rows.length} onClick={copyAll}>Copy all emails</Button>
        <Button icon={<FontAwesomeIcon icon={faDownload} />} disabled={!rows.length} onClick={download}>Download CSV</Button>
      </Space>
    </div>
    <p className="discuss-review-note">People asked for one email, when Discuss leaves beta. Don’t add them to newsletters. Delete the list once that email has gone out.</p>
    <Table<DiscussInterest> rowKey="email" columns={columns} dataSource={rows} loading={list.isFetching} pagination={{ pageSize: 50, hideOnSinglePage: true }} scroll={{ x: 800 }}
      locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={<><strong>No sign-ups yet</strong><p>Emails from “Keep me posted” on ourora.in/discuss and at the end of a discussion appear here.</p></>} /> }} />
  </>;
}
